import { useEffect } from 'react';
import useSystem from 'hooks/business/system/useSystem';
import { Modal, Radio } from 'antd';
import useModal from 'hooks/useModal';
import chatttSDK from 'services/chattt';
import { useTranslate } from '@tolgee/react';
import authorizationCache from 'services/config/authorizationCache';
import { useBeforeLogout, useLogoutOfflineProtect } from 'hooks/useLogout';
import { compare } from 'compare-versions';
import { getVersion } from 'services/chattt';
import { useMemoizedFn } from 'ahooks';

const isSupportBeforeAppQuit = getVersion() && compare(getVersion(), '2.38.0', '>=');

function getQuitFunc() {
  const {
    app: { quit } = {
      quit: undefined,
    },
  } = chatttSDK || {};

  return quit;
}

function CloseClient() {
  const logoutOfflineProtect = useLogoutOfflineProtect();
  const { systemSetting, handleCloseSettingChange } = useSystem();
  const { t } = useTranslate();

  const { closeType } = systemSetting;

  const {
    visible,
    show,
    hide,
    update,
    meta = 'hidden',
  } = useModal(false, closeType === 'default' ? 'hidden' : closeType);

  const defaultHandler = useMemoizedFn(() => {
    const { storage = sessionStorage, cacheKey = `@${import.meta.env.VITE_NAME}-token` } =
      authorizationCache;

    const cacheToken = storage.getItem(cacheKey);

    if (cacheToken) {
      show();
    } else {
      getQuitFunc()?.(true);
    }
  });

  const closeHandler = useMemoizedFn(() => {
    if (closeType === 'closeAndOffline') {
      logoutOfflineProtect(false);
    }
    chatttSDK?.app.quit(true);
  });

  const beforeLogout = useBeforeLogout();

  useEffect(() => {
    const handler = (e: any) => {
      const { detail, timeStamp } = e || {};
      const { trigger } = detail || {};

      if (isSupportBeforeAppQuit) {
        chatttSDK?.app?.appQuitAck?.(timeStamp);
      }

      if (closeType === 'hidden' && trigger !== 'quit') {
        chatttSDK?.app.hide();
        return;
      }

      const onClose = () => {
        if (closeType === 'default') {
          defaultHandler();
        } else {
          closeHandler();
        }
      };

      if (!isSupportBeforeAppQuit) {
        onClose();
        return;
      }

      beforeLogout(onClose);
    };

    window.addEventListener('before-app-quit', handler);

    return () => {
      window.removeEventListener('before-app-quit', handler);
    };
  }, [closeType, beforeLogout, closeHandler, defaultHandler]);

  return (
    <Modal
      title={t('sys_setting_quit_title')}
      visible={visible}
      width={380}
      onCancel={() => hide()}
      onOk={() => {
        handleCloseSettingChange(meta);

        hide();

        setTimeout(() => {
          const quit = getQuitFunc();

          if (typeof quit === 'function') {
            if (meta === 'closeAndOffline') {
              logoutOfflineProtect(false);
            }
            quit(meta === 'close' || meta === 'closeAndOffline');
          }
        }, 100);
      }}
    >
      <Radio.Group
        value={meta}
        onChange={(e) => {
          const value = e.target.value;
          if (value === 'closeAndOffline') {
            Modal.confirm({
              content: t('sys_setting_quit_offline_msg'),
              onOk() {
                update(true, value);
              },
            });
          } else {
            update(true, value);
          }
        }}
      >
        <Radio value="hidden">{t('sys_setting_quit_hidden')}</Radio>
        <Radio value="close">{t('sys_setting_quit_close')}</Radio>
        <Radio value="closeAndOffline">{t('sys_setting_quit_offline')}</Radio>
      </Radio.Group>
    </Modal>
  );
}

export default CloseClient;
